<template>
  <div class="forgot-password-page">
    <div class="forgot-password-container">
      <div class="forgot-password-header">
        <h1 class="page-title">Reset Your Password</h1>
        <p class="page-subtitle">
          Enter your email address and we'll send you instructions to reset your password.
        </p>
      </div>

      <div class="forgot-password-card">
        <div v-if="success" class="success-message">
          <div class="success-icon">✓</div>
          <h2>Email Sent!</h2>
          <p>{{ successMessage }}</p>
          <router-link to="/" class="back-to-home-link">
            Return to Home
          </router-link>
        </div>

        <div v-else>
          <div v-if="error" class="error-message">
            <strong>Oops!</strong> {{ error }}
          </div>

          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Sending reset instructions...</p>
          </div>

          <form v-else @submit.prevent="handleForgotPassword" class="forgot-password-form">
            <div class="form-group">
              <label for="email">Email Address</label>
              <input
                type="email"
                id="email"
                v-model="email"
                required
                placeholder="your.email@example.com"
                class="form-input"
              />
            </div>

            <button type="submit" class="submit-button">
              Send Reset Instructions
            </button>

            <div class="back-to-login">
              <router-link to="/">
                ← Back to Sign In
              </router-link>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { requestPasswordReset } from '../../services/authService.js'
import { showErrorModal } from '../../services/errorHandler.js'

export default {
  name: 'ForgotPassword',
  data() {
    return {
      email: '',
      loading: false,
      error: null,
      success: false,
      successMessage: ''
    }
  },
  methods: {
    async handleForgotPassword() {
      this.loading = true
      this.error = null

      try {
        await requestPasswordReset(this.email)
        this.success = true
        this.successMessage = 'If an account exists with that email address, you will receive password reset instructions shortly.'
      } catch (error) {
        this.error = error.message || 'Failed to send reset instructions. Please try again.'
        showErrorModal([this.error])
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.forgot-password-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: var(--color-background-main);
}

.forgot-password-container {
  width: 100%;
  max-width: 500px;
}

.forgot-password-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 2rem;
  color: var(--color-text-primary);
  margin-bottom: 10px;
  font-weight: 600;
}

.page-subtitle {
  color: var(--color-text-secondary);
  font-size: 1rem;
  line-height: 1.5;
}

.forgot-password-card {
  background: var(--color-surface-light);
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.success-message {
  text-align: center;
  padding: 20px;
}

.success-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--color-success-main);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 20px;
  font-weight: bold;
}

.success-message h2 {
  color: var(--color-text-primary);
  margin-bottom: 15px;
  font-size: 1.5rem;
}

.success-message p {
  color: var(--color-text-secondary);
  margin-bottom: 25px;
  line-height: 1.6;
}

.back-to-home-link {
  display: inline-block;
  color: var(--color-primary-main);
  text-decoration: none;
  font-weight: 600;
  transition: color var(--transition-normal);
}

.back-to-home-link:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}

.error-message {
  background-color: var(--color-error-light);
  color: var(--color-error-dark);
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid var(--color-error-main);
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-secondary);
}

.spinner {
  border: 3px solid var(--color-border-light);
  border-top: 3px solid var(--color-primary-main);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.forgot-password-form {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.submit-button {
  width: 100%;
  padding: 14px 20px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.submit-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 165, 169, 0.3);
}

.submit-button:active {
  transform: translateY(0);
}

.back-to-login {
  text-align: center;
  margin-top: 10px;
}

.back-to-login a {
  color: var(--color-primary-main);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color var(--transition-normal);
}

.back-to-login a:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}
</style>
